import React from 'react';
import { PlaybookAdherence, SalesIntelligence, getCoveragePctClasses, getBuyingSignalColor } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';

interface AdvanceDealSummaryCardProps {
  playbook?: PlaybookAdherence;
  salesIntelligence?: SalesIntelligence;
  closeAttemptTimestamp?: string;
  onViewDetails: () => void;
  onViewInTimeline?: () => void;
}

const AdvanceDealSummaryCard: React.FC<AdvanceDealSummaryCardProps> = ({
  playbook,
  salesIntelligence,
  closeAttemptTimestamp,
  onViewDetails,
  onViewInTimeline,
}) => {
  const hasGeneric = !!playbook?.deal_progression_generic;
  const hasPlaybook = playbook?.was_used && playbook?.deal_progression_coverage;
  const coveragePct = playbook?.deal_progression_coverage?.coverage_pct;
  const genericScore = playbook?.deal_progression_generic?.score;
  const closingSignal = salesIntelligence?.closing_signal;
  const closingScore = salesIntelligence?.closing_signal_score;

  const displayScore = hasPlaybook && coveragePct != null ? coveragePct : genericScore;

  const badgeColor =
    displayScore == null
      ? 'bg-gray-100 text-gray-500 border border-gray-200'
      : displayScore >= 70
      ? 'bg-green-100 text-green-700 border border-green-200'
      : displayScore >= 40
      ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
      : 'bg-red-100 text-red-700 border border-red-200';

  const statusBadge = displayScore != null ? (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${badgeColor}`}>{displayScore}%</span>
  ) : undefined;

  const coachingText = playbook?.deal_progression_generic?.coaching;
  const coachingItems: string[] = [];
  if (coachingText) coachingItems.push(coachingText);

  const missingMsg =
    !playbook && !salesIntelligence
      ? 'Deal advancement analysis not available. Re-analyze this session.'
      : !hasGeneric && !closingSignal
      ? 'Re-analyze this session to see deal advancement analysis.'
      : undefined;

  const resultContent = (
    <div className="space-y-3">
      {/* Closing signal */}
      {closingSignal && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex-shrink-0">
            Closing Signal
          </span>
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded capitalize ${getBuyingSignalColor(closingSignal)}`}
          >
            {closingSignal.replace(/_/g, ' ')}
          </span>
          {typeof closingScore === 'number' && (
            <span className="text-[10px] text-gray-400 ml-auto">({Math.round(closingScore)}%)</span>
          )}
        </div>
      )}

      {/* Playbook deal progression coverage */}
      {hasPlaybook && playbook.deal_progression_coverage && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider">Playbook Coverage</p>
            <span className="text-xs font-bold text-blue-800">
              {playbook.deal_progression_coverage.total_delivered}/{playbook.deal_progression_coverage.total_available} points
            </span>
          </div>
          <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${getCoveragePctClasses(playbook.deal_progression_coverage.coverage_pct)}`}
              style={{ width: `${playbook.deal_progression_coverage.coverage_pct}%` }}
            />
          </div>
          {playbook.deal_progression_coverage.progression_summary && (
            <p className="text-xs text-blue-900 mt-2">{playbook.deal_progression_coverage.progression_summary}</p>
          )}
          {(playbook.deal_progression_coverage.missed_progression_points?.length ?? 0) > 0 && (
            <div className="mt-2 pt-2 border-t border-blue-200">
              <p className="text-[10px] text-red-600 font-semibold mb-1">Missed points:</p>
              <ul className="space-y-0.5">
                {playbook.deal_progression_coverage.missed_progression_points!.slice(0, 2).map((point, idx) => (
                  <li key={idx} className="text-[11px] text-red-700">• {point}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Generic deal progression analysis */}
      {playbook?.deal_progression_generic && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Generic Analysis</p>
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${badgeColor}`}>{genericScore}%</span>
          </div>
          <p className="text-xs text-gray-700">{playbook.deal_progression_generic.summary}</p>
          {(playbook.deal_progression_generic.what_worked || playbook.deal_progression_generic.what_missed) && (
            <div className="mt-2 space-y-1 pt-2 border-t border-gray-200">
              {playbook.deal_progression_generic.what_worked && (
                <p className="text-[11px] text-green-700">
                  <span className="font-semibold">Worked: </span>{playbook.deal_progression_generic.what_worked}
                </p>
              )}
              {playbook.deal_progression_generic.what_missed && (
                <p className="text-[11px] text-red-600">
                  <span className="font-semibold">Missed: </span>{playbook.deal_progression_generic.what_missed}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {!closingSignal && !hasGeneric && !hasPlaybook && (
        <p className="text-xs text-gray-500">No deal advancement data available for this session.</p>
      )}
    </div>
  );

  return (
    <SummarySectionCard
      title="Advance Deal"
      icon={<span>&#128200;</span>}
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={coachingItems.length > 0 ? coachingItems : undefined}
      sectionKey="advance_deal"
      onViewDetails={onViewDetails}
      onViewInTimeline={closeAttemptTimestamp ? onViewInTimeline : undefined}
      missingDataMessage={missingMsg}
      className="col-span-full"
    />
  );
};

export default AdvanceDealSummaryCard;
